/**
 * Application Theme
 *
 * This file contains all the color definitions and theme settings
 * used throughout the application. Modify these values to change
 * the color scheme globally.
 */
export const theme = {
  // Brand colors
  colors: {
    // Primary brand colors using Tailwind's conventional scale
    primary: {
      50: '#FFF7ED',
      100: '#FEF1DF',
      200: '#F7E2C2',
      300: '#ECD0A2',
      400: '#DFBC83',
      500: '#A68557', // updated
      600: '#8A6F48', // previously 700
      700: '#6F5839', // previously 800
      800: '#57442B', // previously 900
      900: '#3E2F1D', // previously 950
      950: '#2A1E13', // new deeper tone
    },
    // Secondary colors
    secondary: {
      50: '#FFF7ED', // Light background
      100: '#FEF1DF',
      200: '#F7E2C2',
      300: '#ECD0A2',
      400: '#DFBC83',
      500: '#A68557', // updated to match primary
      600: '#8A6F48',
      700: '#6F5839',
      800: '#57442B',
      900: '#3E2F1D',
    },
    // Persona-specific colors
    persona: {
      university: '#8A6F48', // Updated to match primary-600
      student: '#8A6F48', // Updated to match primary-600
      admin: '#8A6F48', // Updated to match primary-600
    },
    // Status colors
    status: {
      active: {
        bg: 'bg-green-100',
        text: 'text-green-800',
      },
      draft: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
      },
      pendingApproval: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
      },
      archived: {
        bg: 'bg-red-100',
        text: 'text-red-800',
      },
      default: {
        bg: 'bg-primary-100',
        text: 'text-primary-800',
      },
    },
    // Alert/notification colors
    alert: {
      info: {
        bg: 'bg-primary-50',
        border: 'border-primary-100',
        text: 'text-primary-800',
        icon: 'text-primary-600',
      },
      success: {
        bg: 'bg-green-50',
        border: 'border-green-100',
        text: 'text-green-800',
        icon: 'text-green-500',
      },
      warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-100',
        text: 'text-yellow-800',
        icon: 'text-yellow-600',
      },
      error: {
        bg: 'bg-red-50',
        border: 'border-red-100',
        text: 'text-red-800',
        icon: 'text-red-500',
      },
    },
    // Text colors
    text: {
      primary: 'text-gray-800',
      secondary: 'text-gray-700',
      tertiary: 'text-gray-600',
      quaternary: 'text-gray-500',
      disabled: 'text-gray-400',
    },
    // Background colors
    background: {
      main: 'bg-primary-50', // Light background
      paper: 'bg-white',
      card: 'bg-white',
    },
    // Border colors
    border: {
      main: 'border-gray-200',
      light: 'border-gray-100',
      focus: 'focus:border-primary-500',
    },
  },
  // Typography
  typography: {
    fontFamily: {
      primary: 'Montserrat, sans-serif',
    },
  },
  // Spacing
  spacing: {
    unit: 4,
  },
  // Breakpoints
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
  // Tailwind utility classes for common patterns
  classes: {
    button: {
      primary: 'bg-primary-500 text-white hover:bg-primary-600',
      secondary:
        'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
    },
    card: {
      base: 'bg-white rounded-lg border border-gray-200 shadow-sm',
      hover: 'hover:shadow-md hover:border-primary-500',
    },
    input: {
      base: 'px-3 py-2 border border-gray-300 rounded-md',
      focus:
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
    },
  },
}
// Export individual color constants for convenience
export const PRIMARY_COLOR = theme.colors.primary[500]
export const PRIMARY_HOVER = theme.colors.primary[600]
export const PRIMARY_LIGHT = theme.colors.primary[50]
// Export status color helper function
export const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return theme.colors.status.active
    case 'Draft':
      return theme.colors.status.draft
    case 'Pending Approval':
      return theme.colors.status.pendingApproval
    case 'Archived':
      return theme.colors.status.archived
    default:
      return theme.colors.status.default
  }
}
